/* eslint-disable @typescript-eslint/no-empty-function */
import React, { memo, useContext, useState, useCallback, FC, useEffect } from 'react'

import { SecondaryPanelContextValueType } from 'utils/types'

const SecondaryPanelContext = React.createContext<SecondaryPanelContextValueType>({
  closePanel: () => {},
  isPanelOpen: false,
  openPanel: () => {},
  panelContent: {},
  setPanelContent: () => {},
  setPanelHeader: () => {},
})

const SecondaryPanelProvider: FC<{ children: React.ReactNode }> = memo(({ children }) => {
  const [isPanelOpen, setIsPanelVisible] = useState(false)
  const [panelContent, setPanelContent] = useState<{ header?: string, content?: any }>()

  const closePanel = useCallback(() => {
    const event = new CustomEvent('closePanel', { cancelable: true })
    document.dispatchEvent(event)
    if (!event.defaultPrevented) {
      setIsPanelVisible(false)
    }
  }, [])

  const openPanel = useCallback(() => setIsPanelVisible(true), [])

  const handleEscPress = useCallback((e) => {
    if (e.key === 'Escape') closePanel()
  }, [])

  const setPanelHeader = (newHeader: string) => {
    if (newHeader !== panelContent?.header) setPanelContent({ ...panelContent, header: newHeader })
  }

  useEffect(() => {
    if (isPanelOpen) {
      window.addEventListener('keydown', handleEscPress)
    }
    return () => {
      window.removeEventListener('keydown', handleEscPress)
    }
  }, [isPanelOpen])

  return (
    <SecondaryPanelContext.Provider
      value={{
        closePanel,
        isPanelOpen,
        openPanel,
        panelContent,
        setPanelContent,
        setPanelHeader,
      }}>
      {children}
    </SecondaryPanelContext.Provider>
  )
})

SecondaryPanelProvider.displayName = 'Secondary Panel context'

const useSecondaryPanel = () => useContext(SecondaryPanelContext)

export { SecondaryPanelProvider, useSecondaryPanel }
